import { all, call, delay, put, takeLatest } from 'redux-saga/effects';
import { api } from '../../configurations/api';
import { httpRequest } from '../types';
import {
    campaignHostEnterpriseGrantAdminUrl,
    campaignHostEnterprisePackageAdminUrl,
    campaignHostPackagesUrl,
    campaignHostSubscriptionCancelUrl,
    campaignHostSubscriptionUpdatePaymentMethodUrl,
    campaignHostSubscriptionsBillingHistoryUrl,
    campaignHostSubscriptionsCreationAccessUrl,
    campaignHostSubscriptionsMineUrl,
    campaignHostSubscriptionsPricingUrl,
    campaignHostSubscriptionsPurchaseLtdUrl,
    campaignHostSubscriptionsSubscribeUrl,
    campaignHostSubscriptionsVerifyFlutterwaveUrl,
    campaignHostSubscriptionsVerifyPaystackUrl,
    supportContactUrl,
} from '../../configurations/api/url';
import type {
    ICampaignHostBillingHistoryResponse,
    ICampaignHostPricingResponse,
    ICampaignHostSubscription,
    ICampaignHostVerifyPaystackResult,
} from '../../types/global/campaignHostSubscription.types';
import { SentryCapture } from '../../analytics/Sentry';
import { showToastMessage } from '../../utils/AppUtils';
import {
    extractCampaignHostPackages,
    normalizeCampaignHostCreationAccess,
    normalizeCampaignHostPricingPayload,
    parseCampaignHostMinePayload,
    parseCampaignHostPricingApiResponse,
    requireApiPayload,
    unwrapApiPayload,
} from '../../utils/campaignHostSubscriptionApi';
import { extractPaystackCheckoutResult } from '../../utils/campaignHostSubscriptionRailUtils';
import { isActiveCampaignHostSubscription } from '../../utils/campaignHostSubscriptionFormat';
import { normalizeEnterprisePackage } from '../../utils/adminCampaignHostEnterprise';
import type {
    IGrantCampaignHostEnterprisePayload,
    IGrantCampaignHostEnterpriseResult,
} from '../../types/global/campaignHostAdmin.types';
import { buildCampaignHostEnterpriseSupportBody } from '../../utils/campaignHostEnterpriseSupportApi';
import type { ICampaignHostEnterpriseSupportPayload } from '../../utils/campaignHostEnterpriseSupportApi';
import {
    cancelCampaignHostSubscription,
    cancelCampaignHostSubscriptionFail,
    cancelCampaignHostSubscriptionSuccess,
    fetchCampaignHostBillingHistory,
    fetchCampaignHostBillingHistoryFail,
    fetchCampaignHostBillingHistorySuccess,
    fetchCampaignHostCreationAccess,
    fetchCampaignHostCreationAccessFail,
    fetchCampaignHostCreationAccessSuccess,
    fetchCampaignHostEnterprisePackage,
    fetchCampaignHostEnterprisePackageFail,
    fetchCampaignHostEnterprisePackageSuccess,
    fetchCampaignHostMine,
    fetchCampaignHostMineFail,
    fetchCampaignHostMineSuccess,
    fetchCampaignHostPackages,
    fetchCampaignHostPackagesFail,
    fetchCampaignHostPackagesSuccess,
    fetchCampaignHostPricing,
    fetchCampaignHostPricingFail,
    fetchCampaignHostPricingSuccess,
    grantCampaignHostEnterprise,
    grantCampaignHostEnterpriseFail,
    grantCampaignHostEnterpriseSuccess,
    purchaseCampaignHostLtd,
    purchaseCampaignHostLtdFail,
    purchaseCampaignHostLtdSuccess,
    subscribeCampaignHostMonthly,
    subscribeCampaignHostMonthlyFail,
    subscribeCampaignHostMonthlySuccess,
    submitCampaignHostEnterpriseSupport,
    submitCampaignHostEnterpriseSupportFail,
    submitCampaignHostEnterpriseSupportSuccess,
    updateCampaignHostPaymentMethod,
    updateCampaignHostPaymentMethodFail,
    updateCampaignHostPaymentMethodSuccess,
    verifyCampaignHostPaystack,
    verifyCampaignHostPaystackFail,
    verifyCampaignHostPaystackSuccess,
} from './actions';

function* fetchPublicPackages(): Generator<any, ICampaignHostPricingResponse['packages'], any> {
    const response = yield call(api, campaignHostPackagesUrl, httpRequest.GET, null, 2);
    return extractCampaignHostPackages(unwrapApiPayload(response));
}

function extractClientSecret(latestInvoice: any): string | null {
    return latestInvoice?.payment_intent?.client_secret ?? latestInvoice?.payment_intent?.clientSecret ?? null;
}

function* pollMineUntilActive(maxAttempts = 12): Generator<any, ICampaignHostSubscription | null, any> {
    for (let i = 0; i < maxAttempts; i++) {
        const response = yield call(api, campaignHostSubscriptionsMineUrl, httpRequest.GET, null, 2);
        const data = parseCampaignHostMinePayload(unwrapApiPayload(response));
        if (isActiveCampaignHostSubscription(data)) {
            return data;
        }
        yield delay(2500);
    }
    return null;
}

function* fetchPackagesSaga(): Generator<any, void, any> {
    try {
        const raw = yield call(fetchPublicPackages);
        const packages = Array.isArray(raw) ? raw : extractCampaignHostPackages(raw);
        yield put(fetchCampaignHostPackagesSuccess(packages));
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(fetchCampaignHostPackagesFail(error));
    }
}

function* fetchPricingSaga(): Generator<any, void, any> {
    try {
        const response = yield call(api, campaignHostSubscriptionsPricingUrl, httpRequest.GET, null, 2);
        let data = parseCampaignHostPricingApiResponse(response);

        if (!data.packages.length) {
            const fallbackPackages = yield call(fetchPublicPackages);
            if (fallbackPackages.length) {
                data = normalizeCampaignHostPricingPayload({ ...data, packages: fallbackPackages });
            }
        }

        yield put(fetchCampaignHostPricingSuccess(data));
    } catch (error: any) {
        SentryCapture(error, 'error');
        try {
            const fallbackPackages = yield call(fetchPublicPackages);
            if (fallbackPackages.length) {
                yield put(
                    fetchCampaignHostPricingSuccess(
                        normalizeCampaignHostPricingPayload({ packages: fallbackPackages }),
                    ),
                );
                return;
            }
        } catch (fallbackError: any) {
            SentryCapture(fallbackError, 'error');
        }
        showToastMessage(error?.data?.message ?? error?.message ?? 'Unable to load subscription plans', 'error');
        yield put(fetchCampaignHostPricingFail(error));
    }
}

function* fetchMineSaga(): Generator<any, void, any> {
    try {
        const response = yield call(api, campaignHostSubscriptionsMineUrl, httpRequest.GET, null, 2);
        const data = parseCampaignHostMinePayload(unwrapApiPayload(response));
        yield put(fetchCampaignHostMineSuccess(data));
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(fetchCampaignHostMineFail(error));
    }
}

function* fetchCreationAccessSaga(): Generator<any, void, any> {
    try {
        const response = yield call(api, campaignHostSubscriptionsCreationAccessUrl, httpRequest.GET, null, 2);
        const data = normalizeCampaignHostCreationAccess(requireApiPayload(response));
        yield put(fetchCampaignHostCreationAccessSuccess(data));
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(fetchCampaignHostCreationAccessFail(error));
    }
}

function* fetchBillingHistorySaga({ payload }: any): Generator<any, void, any> {
    try {
        const params = new URLSearchParams();
        if (payload?.subscriptionId) params.set('subscriptionId', payload.subscriptionId);
        if (payload?.limit != null) params.set('limit', String(payload.limit));
        if (payload?.skip != null) params.set('skip', String(payload.skip));
        const qs = params.toString();
        const url = qs
            ? `${campaignHostSubscriptionsBillingHistoryUrl}?${qs}`
            : campaignHostSubscriptionsBillingHistoryUrl;
        const response = yield call(api, url, httpRequest.GET, null, 2);
        const data = requireApiPayload<ICampaignHostBillingHistoryResponse>(response);
        yield put(fetchCampaignHostBillingHistorySuccess(data));
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(fetchCampaignHostBillingHistoryFail(error));
    }
}

function* subscribeMonthlySaga(action: any): Generator<any, void, any> {
    const { payload, resolve, reject } = action;
    try {
        const response = yield call(api, campaignHostSubscriptionsSubscribeUrl, httpRequest.POST, payload, 2);
        const data = unwrapApiPayload<Record<string, unknown>>(response) ?? {};

        const paystack = extractPaystackCheckoutResult(data);
        if (paystack) {
            yield put(subscribeCampaignHostMonthlySuccess({ subscription: null, clientSecret: null }));
            resolve?.(paystack);
            return;
        }

        const clientSecret = extractClientSecret(data?.latest_invoice);

        if (!clientSecret && payload?.paymentMethodId) {
            const active = yield call(pollMineUntilActive);
            yield put(subscribeCampaignHostMonthlySuccess({ subscription: active, clientSecret: null }));
            if (active) {
                showToastMessage('Subscription active', 'success');
                resolve?.({ subscription: active, clientSecret: null });
                return;
            }
            const err = new Error('Subscription is still processing. Refresh Settings in a moment.');
            reject?.(err);
            return;
        }

        yield put(
            subscribeCampaignHostMonthlySuccess({
                subscription: null,
                clientSecret,
            }),
        );
        resolve?.({ clientSecret, data, package: data?.package });
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(subscribeCampaignHostMonthlyFail(error));
        reject?.(error);
    }
}

function* purchaseLtdSaga(action: any): Generator<any, void, any> {
    const { payload, resolve, reject } = action;
    try {
        const response = yield call(api, campaignHostSubscriptionsPurchaseLtdUrl, httpRequest.POST, payload ?? {}, 2);
        const data = requireApiPayload<Record<string, unknown>>(response);

        const paystack = extractPaystackCheckoutResult(data);
        if (paystack) {
            yield put(
                purchaseCampaignHostLtdSuccess({
                    clientSecret: '',
                    paymentIntentId: '',
                    subscription: null,
                }),
            );
            resolve?.(paystack);
            return;
        }

        const clientSecretRaw = data.clientSecret ?? data.client_secret;
        const clientSecret = typeof clientSecretRaw === 'string' ? clientSecretRaw : null;
        if (!clientSecret) {
            throw new Error('Missing payment client secret');
        }
        const paymentIntentId = String(data.paymentIntentId ?? data.payment_intent_id ?? '');
        yield put(
            purchaseCampaignHostLtdSuccess({
                clientSecret,
                paymentIntentId,
                subscription: null,
            }),
        );
        resolve?.(data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(purchaseCampaignHostLtdFail(error));
        reject?.(error);
    }
}

function* verifyPaystackSaga(action: any): Generator<any, void, any> {
    const { payload, resolve, reject } = action;
    try {
        const useFlutterwave =
            payload.provider === 'flutterwave' || Boolean(payload.transaction_id) || Boolean(payload.tx_ref);
        const url = useFlutterwave
            ? campaignHostSubscriptionsVerifyFlutterwaveUrl
            : campaignHostSubscriptionsVerifyPaystackUrl;
        const body: Record<string, string> = {};
        if (payload.transaction_id) body.transaction_id = payload.transaction_id;
        if (payload.tx_ref) body.tx_ref = payload.tx_ref;
        if (payload.reference) body.reference = payload.reference;
        const response = yield call(api, url, httpRequest.POST, body, 2);
        const data = requireApiPayload<ICampaignHostVerifyPaystackResult>(response);
        if (data.subscription && isActiveCampaignHostSubscription(data.subscription)) {
            yield put(fetchCampaignHostMineSuccess(data.subscription));
        } else {
            yield call(fetchMineSaga);
        }
        yield put(verifyCampaignHostPaystackSuccess(data));
        resolve?.(data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(verifyCampaignHostPaystackFail(error));
        reject?.(error);
    }
}

function* cancelSubscriptionSaga(action: any): Generator<any, void, any> {
    const { payload, resolve, reject } = action;
    try {
        const response = yield call(
            api,
            campaignHostSubscriptionCancelUrl(payload.subscriptionId),
            httpRequest.PATCH,
            { cancelImmediately: payload.cancelImmediately ?? false },
            2,
        );
        const data = requireApiPayload<ICampaignHostSubscription>(response);
        yield put(cancelCampaignHostSubscriptionSuccess(data));
        showToastMessage('Subscription updated', 'success');
        resolve?.(data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        const message =
            error?.data?.err?.response?.body?.message ??
            error?.data?.message ??
            error?.message ??
            'Unable to cancel subscription';
        showToastMessage(message, 'error');
        yield put(cancelCampaignHostSubscriptionFail(error));
        reject?.(error);
    }
}

function* updatePaymentMethodSaga(action: any): Generator<any, void, any> {
    const { payload, resolve, reject } = action;
    try {
        const response = yield call(
            api,
            campaignHostSubscriptionUpdatePaymentMethodUrl(payload.subscriptionId),
            httpRequest.PATCH,
            payload.paymentMethodId ? { paymentMethodId: payload.paymentMethodId } : {},
            2,
        );
        const data = requireApiPayload<ICampaignHostSubscription | { provider?: string; manage_payment_url?: string }>(
            response,
        );

        if (
            data &&
            typeof data === 'object' &&
            'provider' in data &&
            (data.provider === 'paystack' || data.provider === 'flutterwave') &&
            data.manage_payment_url
        ) {
            resolve?.(data);
            return;
        }

        yield put(updateCampaignHostPaymentMethodSuccess(data as ICampaignHostSubscription));
        showToastMessage('Payment method updated', 'success');
        resolve?.(data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        const message = error?.data?.message ?? error?.message ?? 'Unable to update payment method';
        showToastMessage(message, 'error');
        yield put(updateCampaignHostPaymentMethodFail(error));
        reject?.(error);
    }
}

export function* watchConfirmCheckoutSuccess(): Generator<any, void, any> {
    /** Called from UI after Stripe confirms — refresh subscription */
    yield call(fetchMineSaga);
    yield call(fetchPricingSaga);
}

function* fetchEnterprisePackageSaga(): Generator<any, void, any> {
    try {
        const response = yield call(api, campaignHostEnterprisePackageAdminUrl, httpRequest.GET, null, 2);
        const data = requireApiPayload(response);
        const pkg = normalizeEnterprisePackage(data);
        if (!pkg) throw new Error('Invalid enterprise package response');
        yield put(fetchCampaignHostEnterprisePackageSuccess(pkg));
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(fetchCampaignHostEnterprisePackageFail(error));
    }
}

function* grantEnterpriseSaga({
    payload,
}: {
    payload: IGrantCampaignHostEnterprisePayload;
}): Generator<any, void, any> {
    try {
        const response = yield call(api, campaignHostEnterpriseGrantAdminUrl, httpRequest.POST, payload, 2);
        const data = requireApiPayload<IGrantCampaignHostEnterpriseResult>(response);
        yield put(grantCampaignHostEnterpriseSuccess(data));
        yield call(showToastMessage, 'Enterprise access granted', 'success');
    } catch (error: any) {
        SentryCapture(error, 'error');
        const message = error?.data?.message ?? error?.message ?? 'Unable to grant enterprise access';
        yield call(showToastMessage, message, 'error');
        yield put(grantCampaignHostEnterpriseFail(error));
    }
}

function* submitEnterpriseSupportSaga({
    payload,
    resolve,
    reject,
}: {
    payload: ICampaignHostEnterpriseSupportPayload;
    resolve?: (v?: void) => void;
    reject?: (e: any) => void;
}): Generator<any, void, any> {
    try {
        const body = buildCampaignHostEnterpriseSupportBody(payload);
        yield call(api, supportContactUrl, 'POST', body);
        yield put(submitCampaignHostEnterpriseSupportSuccess());
        if (typeof resolve === 'function') resolve();
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(submitCampaignHostEnterpriseSupportFail(error));
        if (typeof reject === 'function') reject(error);
    }
}

export default function* campaignHostSubscriptionSaga() {
    // redux-act: listen with action creators (not types.* strings) so dispatch(fetchCampaignHostPricing()) runs sagas
    yield all([
        takeLatest(fetchCampaignHostPackages, fetchPackagesSaga),
        takeLatest(fetchCampaignHostPricing, fetchPricingSaga),
        takeLatest(fetchCampaignHostMine, fetchMineSaga),
        takeLatest(fetchCampaignHostCreationAccess, fetchCreationAccessSaga),
        takeLatest(fetchCampaignHostBillingHistory, fetchBillingHistorySaga),
        takeLatest(subscribeCampaignHostMonthly, subscribeMonthlySaga),
        takeLatest(purchaseCampaignHostLtd, purchaseLtdSaga),
        takeLatest(cancelCampaignHostSubscription, cancelSubscriptionSaga),
        takeLatest(updateCampaignHostPaymentMethod, updatePaymentMethodSaga),
        takeLatest(verifyCampaignHostPaystack, verifyPaystackSaga),
        takeLatest(fetchCampaignHostEnterprisePackage, fetchEnterprisePackageSaga),
        takeLatest(grantCampaignHostEnterprise, grantEnterpriseSaga),
        takeLatest(submitCampaignHostEnterpriseSupport as any, submitEnterpriseSupportSaga),
    ]);
}
